import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { CollapseCard } from '@components/card/collapse-card';
import { EmptyState } from '@components/empty-state';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { TabsHeader } from '@components/header/tabs-header';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconEdit } from '@components/icons/edit';
import { IconEndpoint } from '@components/icons/endpoint';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconStatisticsBar } from '@components/icons/icon-statistics-bar';
import { IconIntegrations } from '@components/icons/integrations';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconReplace } from '@components/icons/replace';
import { IconTriggerOff } from '@components/icons/trigger-off';
import { InfoBox } from '@components/info-box';
import { IntegrationIcon } from '@components/integrations/common';
import { Authentication } from '@components/integrations/integrations-types';
import { LabelTextGroup } from '@components/label-text-group';
import { Spinner } from '@components/loading-spinner';
import { RelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Menu } from '@components/menu';
import { PageContent } from '@components/page-containers/page-container';
import { MiddleColumnPage } from '@components/page-containers/page-containers';
import { Separator } from '@components/separator';
import { Shapes } from '@components/shapes';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import {
  useDisconnectIntegration,
  useMarketplaceTemplate,
  useTemplateIntegrationJourneys,
  useTestIntegrationConnection,
} from 'api/app-marketplace/hooks';
import { ConnectionStatus, MarketplaceTemplate } from 'api/app-marketplace/requests';
import { JourneyListItemType } from 'api/types';
import { JourneyListItem } from 'ui/authenticated/main/pages/journeys/journey-item';
import { routePaths } from 'ui/authenticated/navigation';

import { AuthLabel, sortAuthenticationsWithOAuthFirst } from './auth-utils';
import { useConnectModal } from './connect-modal';

const BulletPoint = ({ text }: { text: string }) => {
  return (
    <Stack orientation="horizontal" align="center">
      <IconCheckmark color="elementBlue100" />
      <Text font="regular14" text={text} />
    </Stack>
  );
};

type TemplateJourneysTabContentProps = {
  journeys?: JourneyListItemType[];
  isLoading: boolean;
};

const TemplateJourneysTabContent = ({ journeys, isLoading }: TemplateJourneysTabContentProps) => {
  const { t } = useScopedTranslate(locale);

  if (isLoading || !journeys) {
    return <Spinner />;
  }

  if (journeys.length === 0) {
    return (
      <PageContent>
        <EmptyState
          skipMargin
          icon={<IconJourneyItem />}
          header={t('noJourneys')}
          content={t('noJourneysDescription')}
        />
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Stack space={2}>
        {journeys.map((journey) => (
          <div key={journey.shortId}>
            <JourneyListItem journey={journey} hideMenu />
          </div>
        ))}
      </Stack>
    </PageContent>
  );
};

type OverviewTabContentProps = {
  template: MarketplaceTemplate;
  needsReconnect: boolean;
  connectionStatus: ConnectionStatus;
};

const OverviewTabContent = ({
  template,
  needsReconnect,
  connectionStatus,
}: OverviewTabContentProps) => {
  const { t } = useScopedTranslate(locale);
  const features = template?.info?.features ?? [];

  return (
    <PageContent>
      <MiddleColumnPage>
        {needsReconnect && template && (
          <InfoBox
            variant="orange"
            icon={<IconFormIndicatorWarning />}
            align="center"
            text={
              connectionStatus === 'credentials_invalid'
                ? t('credentialsInvalidWarning')
                : t('disconnectedWarning')
            }
          />
        )}
        <CollapseCard
          space={6}
          renderCollapse={() => (
            <Stack space={6}>
              <Separator />
              <div>
                <Text font="semibold14" text={t('withIntegration')} />
                {features.map((feature) => (
                  <BulletPoint key={feature} text={feature} />
                ))}
                {features.length === 0 && (
                  <Text font="regular14" color="purple50" text={t('notSet')} />
                )}
              </div>
              <Stack space={4}>
                <LabelTextGroup label={t('builtBy')} text={template.info?.builtBy} />
                <LabelTextGroup label={t('docs')} text={template.info?.docs} />
                <LabelTextGroup label={t('website')} text={template.info?.website} />
                {template.connectedBy && (
                  <LabelTextGroup
                    label={t('connectedBy')}
                    text={
                      template.connectedAt
                        ? `${template.connectedBy} · ${abbreviateDate(
                            template.connectedAt,
                            'en',
                            'datetime'
                          )}`
                        : template.connectedBy
                    }
                  />
                )}
              </Stack>
            </Stack>
          )}
        >
          <Stack space={4} orientation="horizontal">
            <Stack space={4} orientation="horizontal" justify="space-between" align="center">
              <IntegrationIcon src={template.logo} />
              <Stack space={1}>
                <Text text={template?.name} font="semibold14" />
                <Text text={template?.description} font="regular12" color="textHelper" />
              </Stack>
            </Stack>
          </Stack>
        </CollapseCard>
      </MiddleColumnPage>
    </PageContent>
  );
};

export const AppMarketplaceDetail = () => {
  const { t } = useScopedTranslate(locale);
  const { templateId } = useParams<{ templateId: string }>();
  const showConnectModal = useConnectModal();
  const showDeleteModal = useDeleteModal();

  const templateQuery = useMarketplaceTemplate(templateId);
  const template = templateQuery.data;
  const disconnectMutation = useDisconnectIntegration(templateId ?? '');
  const testConnectionMutation = useTestIntegrationConnection();
  const journeysQuery = useTemplateIntegrationJourneys(templateId);

  const connectionStatus = template?.connectionStatus ?? 'not_connected';
  const isConnected = connectionStatus === 'connected';
  const needsReconnect =
    connectionStatus === 'disconnected' || connectionStatus === 'credentials_invalid';
  const hasEverConnected = connectionStatus !== 'not_connected';

  const getStatusText = (status: ConnectionStatus) => {
    switch (status) {
      case 'connected':
        return t('connected');
      case 'disconnected':
        return t('disconnected');
      case 'credentials_invalid':
        return t('disconnected');
      default:
        return t('notConnected');
    }
  };

  const getStatusColor = (status: ConnectionStatus) => {
    switch (status) {
      case 'connected':
        return 'elementGreen200';
      case 'disconnected':
      case 'credentials_invalid':
        return 'elementRed100';
      default:
        return 'textHelper';
    }
  };

  const handleDisconnect = () => {
    if (!template?.connectedIntegrationId) return;
    showDeleteModal({
      title: t('disconnectTitle'),
      description: t('disconnectDescription'),
      buttonText: t('disconnect'),
      buttonIcon: <IconTriggerOff />,
      hideCannotBeUndone: true,
      onConfirm: async () => {
        await disconnectMutation.mutateAsync(template.connectedIntegrationId!);
      },
    });
  };

  const handleTestConnection = async () => {
    if (!template?.connectedIntegrationId) return;
    await testConnectionMutation.mutateAsync(template.connectedIntegrationId);
  };

  const handleEditConnection = () => {
    if (!template) return;
    const authType = template.connectionDetails?.authType;
    const auth =
      template.authentications.find((a) => a.authType === authType) ?? template.authentications[0];
    showConnectModal({ template, authentication: auth, isEdit: true });
  };

  const handleReconnect = () => {
    if (!template) return;
    const authType = template.connectionDetails?.authType;
    const auth =
      template.authentications.find((a) => a.authType === authType) ?? template.authentications[0];
    showConnectModal({ template, authentication: auth, isEdit: true });
  };

  const breadcrumbs = useMemo(
    () => [
      { label: t('appMarketplace'), link: routePaths.appMarketplace },
      { label: template?.name ?? t('loading') },
    ],
    [t, template]
  );

  const renderActions = () => {
    if (!template) return undefined;

    if (isConnected) {
      return (
        <Stack orientation="horizontal" space={4} align="center">
          <Stack orientation="horizontal" space={2} align="center">
            <Text
              font="semibold14"
              text={getStatusText(connectionStatus)}
              color={getStatusColor(connectionStatus)}
            />
            <Shapes.Circle size={8} color={getStatusColor(connectionStatus)} />
          </Stack>
          <Menu
            dataTestid="marketplace-detail-menu-button"
            buttonProps={{
              variant: 'secondary',
              iconLeft: <IconMoreVertical />,
            }}
          >
            {({ close }) => (
              <>
                <Menu.Item
                  label={t('editConnection')}
                  icon={<IconEdit />}
                  onSelect={() => {
                    handleEditConnection();
                    close();
                  }}
                />
                {template.validationUrl && !template.skipCredentialValidation && (
                  <Menu.Item
                    label={t('testConnection')}
                    icon={<IconEndpoint />}
                    onSelect={() => {
                      handleTestConnection();
                      close();
                    }}
                  />
                )}
                <Menu.Item
                  label={t('disconnect')}
                  variant="danger"
                  icon={<IconTriggerOff />}
                  onSelect={() => {
                    handleDisconnect();
                    close();
                  }}
                />
              </>
            )}
          </Menu>
        </Stack>
      );
    }

    if (needsReconnect) {
      return (
        <Stack orientation="horizontal" space={4} align="center">
          <Stack orientation="horizontal" space={2} align="center">
            <Text
              font="semibold14"
              text={getStatusText(connectionStatus)}
              color={getStatusColor(connectionStatus)}
            />
            <Shapes.Circle size={8} color={getStatusColor(connectionStatus)} />
          </Stack>
          {template.authentications.length > 1 ? (
            <Menu
              width="auto"
              buttonProps={{
                variant: 'primary-blue',
                text: t('reconnect'),
                iconLeft: <IconReplace />,
              }}
            >
              {({ close }) =>
                sortAuthenticationsWithOAuthFirst(template.authentications).map((auth) => (
                  <Menu.Item
                    key={auth.shortId}
                    label={
                      <AuthLabel
                        authType={auth.authType as Authentication['authType']}
                        authentications={template.authentications}
                      />
                    }
                    onSelect={() => {
                      showConnectModal({ template, authentication: auth, isEdit: true });
                      close();
                    }}
                  />
                ))
              }
            </Menu>
          ) : (
            <Button
              variant="primary-blue"
              text={t('reconnect')}
              iconLeft={<IconReplace />}
              onClick={handleReconnect}
            />
          )}
        </Stack>
      );
    }

    // Not connected
    return (
      <Stack orientation="horizontal" space={2} align="center">
        <Text
          font="semibold14"
          text={getStatusText(connectionStatus)}
          color={getStatusColor(connectionStatus)}
        />
        <Shapes.Circle size={8} color={getStatusColor(connectionStatus)} />
        {template.authentications.length > 1 ? (
          <Menu
            width="auto"
            buttonProps={{
              variant: 'primary-blue',
              text: t('connect'),
            }}
          >
            {({ close }) =>
              sortAuthenticationsWithOAuthFirst(template.authentications).map((auth) => (
                <Menu.Item
                  key={auth.shortId}
                  label={
                    <AuthLabel
                      authType={auth.authType as Authentication['authType']}
                      authentications={template.authentications}
                    />
                  }
                  onSelect={() => {
                    showConnectModal({ template, authentication: auth });
                    close();
                  }}
                />
              ))
            }
          </Menu>
        ) : (
          <Button
            variant="primary-blue"
            text={t('connect')}
            onClick={() => {
              showConnectModal({ template, authentication: template.authentications[0] });
            }}
          />
        )}
      </Stack>
    );
  };

  const tabs = useMemo(
    () => [
      {
        id: 'overview',
        label: t('overview'),
        icon: <IconStatisticsBar />,
        content: template ? (
          <OverviewTabContent
            template={template}
            needsReconnect={needsReconnect}
            connectionStatus={connectionStatus}
          />
        ) : (
          <RelativeSpinner />
        ),
      },
      {
        id: 'journeys',
        label: t('journeys'),
        icon: <IconJourneyItem />,
        counter: journeysQuery.data?.length,
        content: (
          <TemplateJourneysTabContent
            journeys={journeysQuery.data}
            isLoading={journeysQuery.isLoading}
          />
        ),
      },
    ],
    [t, template, needsReconnect, connectionStatus, journeysQuery.data, journeysQuery.isLoading]
  );

  if (hasEverConnected) {
    return (
      <TabsHeader
        backAction={<IconIntegrations color="elementPurple50" />}
        breadcrumbs={<Breadcrumb items={breadcrumbs} />}
        actions={renderActions()}
        tabs={tabs}
        isLoading={!template}
      />
    );
  }

  return (
    <>
      <Header
        backAction={<IconIntegrations color="elementPurple50" />}
        breadcrumbs={<Breadcrumb items={breadcrumbs} />}
        actions={renderActions()}
      />
      {template ? (
        <OverviewTabContent
          template={template}
          needsReconnect={false}
          connectionStatus={connectionStatus}
        />
      ) : (
        <PageContent>
          <MiddleColumnPage>
            <RelativeSpinner />
          </MiddleColumnPage>
        </PageContent>
      )}
    </>
  );
};

export const TemplateInfoCard = ({ template }: { template: MarketplaceTemplate }) => {
  return (
    <Stack space={4} orientation="horizontal">
      <Stack space={4} orientation="horizontal" justify="space-between" align="center">
        <IntegrationIcon src={template.logo} />
      </Stack>
    </Stack>
  );
};

const locale = {
  en: {
    appMarketplace: 'App marketplace',
    loading: 'Loading',
    connected: 'Connected',
    disconnected: 'Disconnected',
    notConnected: 'Not connected',
    connect: 'Connect',
    reconnect: 'Reconnect',
    editConnection: 'Edit connection',
    testConnection: 'Test connection',
    disconnect: 'Disconnect',
    disconnectTitle: 'Disconnect integration',
    disconnectDescription: 'Are you sure you want to disconnect this integration?',
    disconnectedWarning:
      'This integration has been disconnected. Please reconnect to re-enable this integration',
    credentialsInvalidWarning:
      'Your credentials have either expired or been removed, please reconnect to re-enable this integration',
    heroCaption: 'App marketplace',
    withIntegration: 'With this integration, you can',
    featuresMissing: 'No features added yet.',
    builtBy: 'Built by',
    docs: 'Docs',
    website: 'Website',
    connectedBy: 'Connected by',
    notSet: 'Not set',
    notFoundTitle: 'Integration not found',
    notFoundDescription:
      'This integration is unavailable. Return to the marketplace to browse apps.',
    backToMarketplace: 'Back to marketplace',
    overview: 'Overview',
    journeys: 'Journeys',
    settings: 'Settings',
    noJourneys: 'No journeys',
    noJourneysDescription: 'This app is currently not used in any journeys you have access to',
    settingsEmpty: 'Settings',
    settingsEmptyDescription: 'No settings available for this app',
  },
};
